import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  InsertDriveFile,
  Delete,
  ContentCopy,
  TimerOutlined,
  DownloadOutlined,
  FolderOpen,
  CachedOutlined,
  RefreshOutlined,
  Archive,
  Image,
  Description,
  VideoFile,
  AudioFile,
  Code,
} from '@mui/icons-material';
import { filesAPI, downloadAPI } from '../../api/api';
import { useSnackbar } from 'notistack';

interface FileMeta {
  id: string;
  name: string;
  size: number;
  type: string;
  uploadDate: string;
  status: string;
  expiryTime: string;
  oneTimeDownload: boolean;
}

const fmtSize = (b: number) => {
  if (!b) return '0 B';
  const k = 1024, s = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return parseFloat((b / Math.pow(k, i)).toFixed(1)) + ' ' + s[i];
};

const fmtDate = (d: string) => {
  const date = new Date(d);
  return date.toLocaleDateString() + ', ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const fmtRelative = (d: string) => {
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000), h = Math.floor(diff / 3600000), dy = Math.floor(diff / 86400000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (dy < 7) return `${dy}d ago`;
  return new Date(d).toLocaleDateString();
};

const expiryLabel = (t: string) => {
  const m: Record<string, string> = { '1d': '1 Day', '7d': '7 Days', '30d': '30 Days', 'never': 'Never' };
  return m[t] || t;
};

const typeShort = (t: string) => {
  if (!t) return 'file';
  const s = t.split('/').pop() || '';
  if (/zip|rar|7z|tar|gz/.test(s)) return 'zip';
  if (/png|jpe?g|gif|webp|svg/.test(s)) return 'image';
  if (/mp4|avi|mkv|mov|webm/.test(s)) return 'video';
  if (/mp3|wav|ogg|flac/.test(s)) return 'audio';
  if (/json|xml|html|css|javascript/.test(s)) return 'code';
  if (/pdf|doc|txt|rtf/.test(s)) return 'doc';
  return s.length > 6 ? 'file' : s;
};

const typeIcon = (t: string) => {
  const sx = { fontSize: 24 };
  switch (typeShort(t)) {
    case 'zip': return <Archive sx={{ ...sx, color: '#FFB74D' }} />;
    case 'image': return <Image sx={{ ...sx, color: '#4FC3F7' }} />;
    case 'video': return <VideoFile sx={{ ...sx, color: '#CE93D8' }} />;
    case 'audio': return <AudioFile sx={{ ...sx, color: '#80CBC4' }} />;
    case 'code': return <Code sx={{ ...sx, color: '#A5D6A7' }} />;
    case 'pdf': case 'doc': return <Description sx={{ ...sx, color: '#EF9A9A' }} />;
    default: return <InsertDriveFile sx={{ ...sx, color: '#90CAF9' }} />;
  }
};

const FileList: React.FC = () => {
  const [files, setFiles] = useState<FileMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [fileToDel, setFileToDel] = useState<FileMeta | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fetch = async () => {
    setLoading(true);
    try {
      const r = await filesAPI.getFiles();
      setFiles(r.data || []);
    } catch { enqueueSnackbar('Failed to load files', { variant: 'error' }); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []); // eslint-disable-line

  const copyLink = async (f: FileMeta) => {
    try {
      const r = await downloadAPI.generateLink(f.id);
      await navigator.clipboard.writeText(`${window.location.origin}${r.data.downloadUrl}`);
      enqueueSnackbar('Link copied!', { variant: 'success' });
    } catch {
      try {
        await navigator.clipboard.writeText(`${window.location.origin}/download/${f.id}/${encodeURIComponent(f.name)}`);
        enqueueSnackbar('Link copied!', { variant: 'info' });
      } catch { enqueueSnackbar('Failed to copy link', { variant: 'error' }); }
    }
  };

  const download = async (f: FileMeta) => {
    try {
      const r = await downloadAPI.generateLink(f.id);
      const a = document.createElement('a');
      a.href = r.data.downloadUrl;
      a.setAttribute('download', f.name);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch { enqueueSnackbar('Download failed', { variant: 'error' }); }
  };

  const confirmDelete = async () => {
    if (!fileToDel) return;
    setDeleting(true);
    try {
      await filesAPI.deleteFile(fileToDel.id);
      setFiles((p) => p.filter((f) => f.id !== fileToDel.id));
      enqueueSnackbar('File deleted', { variant: 'success' });
    } catch { enqueueSnackbar('Delete failed', { variant: 'error' }); }
    finally { setDeleting(false); setDeleteOpen(false); setFileToDel(null); }
  };

  /* --- MOBILE CARD VIEW --- */
  const MobileView = () => (
    <Stack spacing={1.5}>
      {files.map((f) => (
        <Card
          key={f.id}
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
          }}
        >
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              {typeIcon(f.type)}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{f.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {fmtSize(f.size)} &bull; {typeShort(f.type)} &bull; {fmtRelative(f.uploadDate)}
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                  <Chip
                    icon={<TimerOutlined sx={{ fontSize: 12 }} />}
                    label={expiryLabel(f.expiryTime)}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.65rem', height: 22, borderColor: alpha(theme.palette.success.main, 0.3), color: 'success.main', '& .MuiChip-icon': { color: 'success.main' } }}
                  />
                  {f.oneTimeDownload && (
                    <Chip label="1x" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 22, borderColor: alpha(theme.palette.warning.main, 0.3), color: 'warning.main' }} />
                  )}
                  <Chip label="Complete" size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' }} />
                </Stack>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
              <Button size="small" startIcon={<ContentCopy sx={{ fontSize: 14 }} />} onClick={() => copyLink(f)} sx={{ fontSize: '0.75rem', color: 'info.main' }}>
                Copy
              </Button>
              <Button size="small" startIcon={<DownloadOutlined sx={{ fontSize: 14 }} />} onClick={() => download(f)} sx={{ fontSize: '0.75rem', color: 'success.main' }}>
                Download
              </Button>
              <Button
                size="small"
                startIcon={<Delete sx={{ fontSize: 14 }} />}
                onClick={() => { setFileToDel(f); setDeleteOpen(true); }}
                sx={{ fontSize: '0.75rem', color: 'error.main' }}
              >
                Delete
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );

  /* --- DESKTOP TABLE VIEW --- */
  const DesktopView = () => (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}
    >
      <Table>
        <TableHead>
          <TableRow>
            {['Name', 'Size', 'Type', 'Uploaded', 'Settings', 'Status', ''].map((h) => (
              <TableCell
                key={h}
                align={h === '' ? 'right' : 'left'}
                sx={{
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'text.secondary',
                  py: 1.5,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {files.map((f) => (
            <TableRow
              key={f.id}
              sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) }, transition: 'background 0.15s' }}
            >
              <TableCell>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {typeIcon(f.type)}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 240 }}>{f.name}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'monospace', fontSize: '0.6rem' }}>
                      ID: {f.id}
                    </Typography>
                  </Box>
                </Stack>
              </TableCell>
              <TableCell><Typography variant="body2">{fmtSize(f.size)}</Typography></TableCell>
              <TableCell><Typography variant="body2">{typeShort(f.type)}</Typography></TableCell>
              <TableCell><Typography variant="body2" color="text.secondary" noWrap>{fmtDate(f.uploadDate)}</Typography></TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5}>
                  <Chip
                    icon={<TimerOutlined sx={{ fontSize: 13 }} />}
                    label={`Expires: ${expiryLabel(f.expiryTime)}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', borderColor: alpha(theme.palette.success.main, 0.35), color: 'success.main', '& .MuiChip-icon': { color: 'success.main' } }}
                  />
                  {f.oneTimeDownload && (
                    <Chip label="1x" size="small" variant="outlined" sx={{ fontSize: '0.7rem', borderColor: alpha(theme.palette.warning.main, 0.35), color: 'warning.main' }} />
                  )}
                </Stack>
              </TableCell>
              <TableCell>
                <Chip label="Complete" size="small" sx={{ fontSize: '0.7rem', fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' }} />
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                  <Tooltip title="Copy Link">
                    <IconButton size="small" onClick={() => copyLink(f)} sx={{ color: 'info.main', '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.08) } }}>
                      <ContentCopy sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download">
                    <IconButton size="small" onClick={() => download(f)} sx={{ color: 'success.main', '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.08) } }}>
                      <DownloadOutlined sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => { setFileToDel(f); setDeleteOpen(true); }} sx={{ color: 'error.main', '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08) } }}>
                      <Delete sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 3 }}
      >
        <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>My Files</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CachedOutlined sx={{ fontSize: 16 }} />}
            onClick={() => { if ('caches' in window) caches.keys().then((n) => n.forEach((k) => caches.delete(k))); enqueueSnackbar('Cache cleared', { variant: 'info' }); }}
            sx={{ fontSize: '0.75rem', borderColor: alpha(theme.palette.primary.main, 0.3), color: 'primary.main' }}
          >
            Clear Cache
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshOutlined sx={{ fontSize: 16 }} />}
            onClick={fetch}
            disabled={loading}
            sx={{ fontSize: '0.75rem', borderColor: alpha(theme.palette.info.main, 0.3), color: 'info.main' }}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : files.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <FolderOpen sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary" variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }}>No files yet</Typography>
          <Typography color="text.disabled" variant="body2" sx={{ mt: 0.5 }}>Upload files to see them here</Typography>
        </Box>
      ) : isMobile ? (
        <MobileView />
      ) : (
        <DesktopView />
      )}

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete File</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{fileToDel?.name}</strong>? This can't be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FileList;
